import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { SavedDataInfo } from 'src/app/models/form/saved-data-info';
import { CategoriesService } from 'src/app/services/api/general-settings/categories.service';
import { LawsService } from 'src/app/services/api/general-settings/laws.service';
import { HttpParams } from '@angular/common/http';
import { AuditLog } from 'src/app/models/audit-log.model';
import { Subject, takeUntil } from 'rxjs';
import { RegulationsChangesLogComponent } from 'src/app/modules/general-settings/regulations/components/regulations-logs-change/regulations-changes-logs.component';

const params = new HttpParams().set('size', '9999');

@Component({
  selector: 'settings-regulation-to-checklist',
  templateUrl: './settings-regulation-to-checklist.component.html',
  styleUrls: ['./settings-regulation-to-checklist.component.scss'],
  imports: [CommonModule, NgbModalModule, RegulationsChangesLogComponent],
})
export class SettingsRegulationToChecklistComponent implements OnInit, OnChanges {
  @Input() jurisdictionId!: number;
  @Input() lawId!: number;
  @Input() checklist!: any;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  fromRegulation: any[] = [];
  toChecklist: any[] = [];
  toChecklistTemp: any[] = [];
  detailRegulation: any = null;
  isSaving = false;
  categoryOptions: { key: number; value: string }[] = [];
  lawsOptions: { key: number; value: string }[] = [];
  selectedCategoryId: number | null = null;
  selectedLawId: number | null = null;
  regulations: any[] = [];
  hasChanges = false;
  auditLogs: AuditLog[] = [];
  private allRegulations: any[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private projectsService: ProjectsService,
    private alertNotificationService: AlertNotificationService,
    private modalService: NgbModal,
    private categoriesService: CategoriesService,
    private lawsService: LawsService
  ) { }

  ngOnInit(): void {
    this.categoriesService.list({ params }).subscribe((response: any) => {
      this.categoryOptions = response.data.categories.map((category: any) => ({ key: category.categoryId, value: category.name }));
    });

    if (this.jurisdictionId) {
      this.loadLaws();
      this.loadRegulations();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jurisdictionId'] && changes['jurisdictionId'].currentValue) {
      this.loadLaws();
      this.loadRegulations();
    }
    if (changes['checklist'] && changes['checklist'].currentValue) {
      this.loadRegulationsToChecklist();
      this.loadAuditLogs(this.checklist?.checklistId);
    }
  }

  loadLaws(): void {
    if (this.jurisdictionId) {
      const params = new HttpParams()
        .set('size', '9999')
        .set('jurisdictionId', this.jurisdictionId.toString());

      this.lawsService.list({ params }).subscribe((response: any) => {
        this.lawsOptions = response.data.laws.map((law: any) => ({ key: law.lawId, value: law.name }));
      });
    }
  }

  filterByCategory(categoryId: number | null): void {
    this.selectedCategoryId = categoryId;
    this.loadRegulations();
  }

  filterByLaw(lawId: number | null): void {
    this.selectedLawId = lawId;
    this.loadRegulations();
  }
  loadRegulations(): void {
    if (this.jurisdictionId) {
      let params = new HttpParams()
        .set('size', '9999')
        .set('jurisdictionId', this.jurisdictionId.toString());

      if (this.selectedLawId !== null) {
        params = params.set('lawId', this.selectedLawId.toString());
      } else if (this.lawId) {
        params = params.set('lawId', this.lawId.toString());
      }

      if (this.selectedCategoryId !== null) {
        params = params.set('categoryId', this.selectedCategoryId.toString());
      }

      this.projectsService.getRegulationList(params);

      this.projectsService.regulations$.subscribe((response: any) => {
        this.regulations = response;
        this.fromRegulation = this.regulations;
      });
    } else {
      console.warn('JurisdictionId must be set to load regulations.');
      this.fromRegulation = [];
      this.toChecklist = [];
    }
  }

  loadRegulationsToChecklist(): void {
    if (this.checklist && this.checklist.checklistId) {
      this.projectsService.listSettingsRegulationToChecklist(this.checklist.checklistId).subscribe((response: any) => {
        if (response && response.data && response.data.regulations) {
          this.toChecklist = response.data.regulations;
        } else {
          this.toChecklist = response;
        }
        this.allRegulations = [...this.toChecklist];
      });
    }
  }

  loadAuditLogs(checklistId: number): void {
    this.projectsService.listSettingsRegulationToChecklistChangelog(checklistId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.auditLogs = response.data?.auditLogs || [];
        },
        error: (error: any) => {
          console.error('Error loading changelog:', error);
          this.auditLogs = [];
        }
      });
  }

  isTemporarilySelected(regulation: any): boolean {
    const regulationId = regulation.regulationId;
    return this.toChecklistTemp.some(r => {
      const tempId = r.regulationId;
      return tempId === regulationId;
    });
  }

  isInChecklist(regulation: any): boolean {
    const regulationId = regulation.regulationId;
    return this.toChecklist.some(r => {
      const listId = r.regulationId;
      return listId === regulationId;
    });
  }

  toggleSelection(regulation: any): void {
    const regulationId = regulation.regulationId;

    if (this.isTemporarilySelected(regulation)) {
      this.toChecklistTemp = this.toChecklistTemp.filter(r => {
        const tempId = r.regulationId;
        return tempId !== regulationId;
      });
    } else {
      this.toChecklistTemp.push(regulation);
    }
    this.hasChanges = this.toChecklistTemp.length > 0;
  }

  removeFromChecklist(regulation: any): void {
    const regulationId = regulation.regulationId

    this.toChecklist = this.toChecklist.filter(r => {
      const listId = r.regulationId
      return listId !== regulationId;
    });

    if (!this.fromRegulation.some(r => {
      const fromId = r.regulationId
      return fromId === regulationId;
    })) {
      this.fromRegulation.push(regulation);
    }
    this.hasChanges = true;
  }

  openDetails(regulation: any, content: any): void {
    this.detailRegulation = regulation;
    this.modalService.open(content, { size: 'lg' });
  }

  saveChecklist(): void {
    this.isSaving = true;

    const allRegulations = [...this.toChecklist, ...this.toChecklistTemp];


    const regulationIds = allRegulations.map(reg => {
      const regulationId = reg.regulationId;
      return { regulationId };
    });

    this.projectsService.updateSettingsRegulationToChecklist(this.checklist.checklistId, { checklistJoinRegulations: regulationIds }).subscribe({
      next: (response) => {
        this.alertNotificationService.success('Checklist saved successfully!');

        this.toChecklist = [...this.toChecklist, ...this.toChecklistTemp];

        this.toChecklistTemp = [];
        this.hasChanges = false;

        this.onSavedData.emit({
          success: true,
          response: response
        });

        this.isSaving = false;
      },
      error: (error) => {
        this.alertNotificationService.error('Failed to save checklist. Please try again.');
        this.isSaving = false;

        this.onSavedData.emit({
          success: false,
          error: error
        });
      }
    });
  }
}
